import React, { useEffect, useMemo, useRef, useState } from "react";
import { CloseButton } from "../../../components/CloseButton";
import { Kbd } from "../../../components/shortcuts/Kbd";
import { defaultParametersFor, Machine } from "../../../game/Machine";
import { stationWorkSpeed } from "../../../game/bench-mounting";
import { machineDustMultiplier } from "../../../game/Dust";
import {
  planReadiness,
  PlanReadiness,
} from "../../../game/bench-work/plan-readiness";
import {
  BenchPlan,
  PLAN_CATEGORIES,
  PlanCategoryId,
  unlockedBenchPlans,
} from "../../../game/bench-work/plan-registry";
import { selectedBenchPlan } from "../../../game/bench-work/tool-work";
import { getOperationDuration } from "../../../game/skill-helpers";
import { formatDuration } from "../../../game/time";
import { classNames } from "../../../utils/classNames";
import {
  BLUEPRINT_BLUE,
  BLUEPRINT_BLUE_DEEP,
  BlueprintSheet,
  ReadinessStamp,
} from "../station/BlueprintStack";
import { BlueprintSchematic } from "../station/BlueprintSchematic";
import {
  clearMachineOperation,
  setMachineOperation,
  shopCellMap,
} from "../../../sim/commands/machine-commands";
import { MachineEntity } from "../../../sim/entities/MachineEntity";
import { useGame, useShopState } from "../../useShell";

/**
 * The plan folder, pulled up out of the drawer: one manila hanging file
 * with a tab along its top edge per category the player's skills have
 * put anything in. The open tab's drawings are filed down the left as
 * a stack of blueprint sheets — the one being read slides out of the
 * stack — and that drawing lies open across the rest of the folder:
 * full schematic, the bill of materials against the shop's stock, the
 * margin note, and a Use tag in its title block: using it selects the
 * plan and puts the folder away.
 * Listing comes from the plan registry, not the bench's mounted tools,
 * so a build whose driver is in the drawers still shows, wearing the
 * missing tool as a shortfall.
 *
 * The folder owns the keyboard while it's up: ←/→ turn to the next
 * tab, ↑/↓ walk the sheets, Enter uses the open drawing, Esc closes
 * (so does another click on the pile).
 *
 * Test contract (tests/machine-panel.ts): each tab is
 * `data-testid="plan-tab"`, each sheet in the open folder carries its
 * plan's name in `data-mode-option` exactly once, and the open
 * drawing's Use tag is `data-testid="pull-plan"`.
 */
export const PlanBrowser: React.FC<{
  machine: Machine;
  bench: MachineEntity;
  onClose: () => void;
}> = ({ machine, bench, onClose }) => {
  const gameState = useShopState();
  const game = useGame();
  const working = machine.state.operationProgress.status === "inProgress";
  const selected = selectedBenchPlan(machine);

  const plans = useMemo(
    () => unlockedBenchPlans(gameState.progression),
    [gameState.progression],
  );
  const readinessById = useMemo(() => {
    const map = new Map<string, PlanReadiness>();
    for (const plan of plans) {
      map.set(plan.operation.id, planReadiness(gameState, machine, plan));
    }
    return map;
  }, [gameState, machine, plans]);

  const categories = PLAN_CATEGORIES.filter((category) =>
    plans.some((plan) => plan.category === category.id),
  );

  // The folder opens on the drawing that's out, or the first one filed
  const [focusedId, setFocusedId] = useState<string | null>(
    selected?.id ?? plans[0]?.operation.id ?? null,
  );
  const focused =
    plans.find((plan) => plan.operation.id === focusedId) ?? plans[0] ?? null;
  const [openTab, setOpenTab] = useState<PlanCategoryId | null>(
    focused?.category ?? categories[0]?.id ?? null,
  );
  const filed = plans.filter((plan) => plan.category === openTab);

  const dustMultiplier = machineDustMultiplier(
    gameState.dust,
    machine,
    gameState.shopInfo.size,
  );
  const workSpeed = stationWorkSpeed(machine, shopCellMap(game));

  const pull = (plan: BenchPlan) => {
    if (working) return;
    setMachineOperation(
      game,
      bench,
      plan.operation,
      defaultParametersFor(plan.operation),
    );
    onClose();
  };

  const putBack = () => {
    if (working) return;
    clearMachineOperation(game, bench);
  };

  // Turning to a tab sets out its first sheet, so the open drawing is
  // always one from the folder that's showing
  const openFolder = (id: PlanCategoryId) => {
    setOpenTab(id);
    const first = plans.find((plan) => plan.category === id);
    if (first) setFocusedId(first.operation.id);
  };

  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRefs = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  const moveFocus = (step: number) => {
    if (filed.length === 0) return;
    const index = filed.findIndex((plan) => plan.operation.id === focusedId);
    const next =
      filed[(index + step + filed.length) % filed.length] ?? filed[0];
    setFocusedId(next.operation.id);
    sheetRefs.current
      .get(next.operation.id)
      ?.scrollIntoView({ block: "nearest" });
  };

  const moveFolder = (step: number) => {
    if (categories.length === 0) return;
    const index = categories.findIndex((category) => category.id === openTab);
    const next =
      categories[(index + step + categories.length) % categories.length] ??
      categories[0];
    openFolder(next.id);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        // Esc closes the folder, never the bench view underneath
        event.stopPropagation();
        onClose();
        return;
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        moveFocus(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        moveFocus(-1);
        return;
      case "ArrowRight":
        event.preventDefault();
        event.stopPropagation();
        moveFolder(1);
        return;
      case "ArrowLeft":
        event.preventDefault();
        event.stopPropagation();
        moveFolder(-1);
        return;
      case "Enter":
        if (focused) {
          event.preventDefault();
          event.stopPropagation();
          if (selected?.id !== focused.operation.id) pull(focused);
        }
        return;
    }
  };

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      data-testid="plan-browser"
      onKeyDown={onKeyDown}
      // Over the whole view, above the coach's column (z-36): the folder
      // is the only thing being read while it is up. The padding keeps it
      // clear of the top bar, the pile's column, and the bottom hints.
      className="pointer-events-auto absolute inset-0 z-[45] flex items-center justify-center bg-ink-black/85 px-32 pb-16 pt-[5.5rem] outline-none"
      onClick={onClose}
    >
      <div
        className="flex h-[min(100%,38rem)] w-full max-w-[58rem] flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        {/* The tabs along the folder's top edge, staggered the way a
            box of hanging files is cut. The open one is the folder's
            own paper and joins its body; the rest sit behind, lower. */}
        <div role="tablist" className="flex items-end gap-1 pl-5">
          {categories.map((category, index) => {
            const open = category.id === openTab;
            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                aria-selected={open}
                data-testid="plan-tab"
                data-category={category.id}
                data-sfx="ui-tab"
                onClick={() => openFolder(category.id)}
                className={classNames(
                  "relative rounded-t-md border border-b-0 px-4 pb-2 pt-2 font-condensed text-[0.8rem] font-bold uppercase tracking-[0.15em] text-ink-black transition-[transform,filter]",
                  // Every tab the same size, so the folder's body holds
                  // still when a different one opens: the closed ones
                  // sit a little lower, behind, and rise on hover
                  open
                    ? "z-10 -mb-px border-paper-manila-edge bg-paper-manila"
                    : "translate-y-[3px] border-black/20 bg-paper-manila-edge text-ink-black/70 hover:translate-y-[1px] hover:brightness-105",
                )}
                style={{
                  // Each tab cut a hair off square, like a punched one
                  rotate: `${index % 2 === 0 ? -0.4 : 0.4}deg`,
                }}
              >
                {category.label}
              </button>
            );
          })}
        </div>

        {/* The folder itself */}
        <div className="relative flex min-h-0 flex-1 gap-6 rounded-b-md rounded-tr-md border border-paper-manila-edge bg-paper-manila p-5 shadow-[0_18px_40px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(255,255,255,0.35)]">
          {/* The folder's fold: the crease where the front flap meets the back */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-black/10" />
          {/* Off the folder's top-right corner, clear of its tabs */}
          <CloseButton label="Put the folder away" onClose={onClose} />

          {/* The sheets filed in the open folder, stacked down the
              left; the one being read slides out of the stack */}
          <ul
            className="flex w-80 shrink-0 flex-col gap-2 overflow-y-auto py-1 pr-3"
            data-testid="plan-folder"
          >
            {filed.map((plan, index) => {
              const readiness = readinessById.get(plan.operation.id);
              const isFocused = focused?.operation.id === plan.operation.id;
              const isSelected = selected?.id === plan.operation.id;
              return (
                <li key={plan.operation.id}>
                  <button
                    ref={(el) => {
                      if (el) sheetRefs.current.set(plan.operation.id, el);
                    }}
                    type="button"
                    data-sfx="ui-page-turn"
                    data-sfx-hover="ui-page-touch"
                    data-testid="plan-thumb"
                    data-plan-id={plan.operation.id}
                    aria-current={isFocused}
                    onClick={() => setFocusedId(plan.operation.id)}
                    onDoubleClick={() => pull(plan)}
                    className={classNames(
                      "relative flex w-full items-center gap-3 rounded-[2px] border py-1.5 pl-2 pr-2.5 text-left transition-transform",
                      isFocused
                        ? "translate-x-2 border-white/80 shadow-[0_4px_10px_rgba(0,0,0,0.45)]"
                        : "border-white/30 shadow-md hover:translate-x-1 hover:border-white/60",
                    )}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? BLUEPRINT_BLUE : BLUEPRINT_BLUE_DEEP,
                      // Filed in a hurry: each sheet a hair crooked, and
                      // the one being read pulled square as it slides out
                      rotate: isFocused
                        ? "0deg"
                        : `${((index * 37) % 5) * 0.3 - 0.6}deg`,
                    }}
                  >
                    {/* The sheet's margin line */}
                    <span className="pointer-events-none absolute inset-1 border border-white/20" />
                    <span className="pointer-events-none flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden">
                      <BlueprintSchematic
                        blueprint={plan.blueprint}
                        className="h-full w-full"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        data-mode-option
                        className="line-clamp-2 font-condensed text-[1rem] font-semibold uppercase leading-tight tracking-wide text-white"
                      >
                        {plan.record.name}
                      </span>
                      <span className="mt-0.5 flex items-center gap-2 font-condensed text-[0.7rem] text-white/60">
                        {readiness && (
                          <ReadinessStamp
                            ready={readiness.ready}
                            className="order-last ml-auto text-[0.5rem]"
                          />
                        )}
                        <span className="tabular-nums">
                          {formatDuration(
                            getOperationDuration(
                              plan.operation,
                              gameState.progression,
                              dustMultiplier,
                              workSpeed,
                            ),
                          )}
                        </span>
                        {isSelected && (
                          <span className="rounded-sm bg-paper-manila px-1 text-[0.55rem] font-bold uppercase tracking-[0.15em] text-ink-black">
                            Out
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* The drawing lifted out and laid open on the folder */}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-1 py-2">
            {focused && (
              <div className="[transform:rotate(-0.5deg)]">
                <BlueprintSheet
                  operation={focused.operation}
                  machine={machine}
                  locked={working}
                  readiness={readinessById.get(focused.operation.id)}
                  note={focused.note}
                  title={focused.record.name}
                  spread
                  // The verb sits in the title block's corner: a manila
                  // tag to use the drawing, or the way back for the one
                  // that's out. A running job keeps the "In progress"
                  // label instead.
                  action={
                    working ? undefined : selected?.id ===
                      focused.operation.id ? (
                      <button
                        type="button"
                        data-testid="put-back-plan"
                        data-sfx="ui-page-turn"
                        onClick={putBack}
                        className="flex shrink-0 items-center gap-1.5 rounded-sm border border-white/60 px-2.5 py-1 font-condensed text-[0.8rem] font-semibold uppercase tracking-[0.15em] text-white hover:bg-white/10"
                      >
                        <span aria-hidden>←</span> Put back
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid="pull-plan"
                        data-sfx="ui-page-turn"
                        onClick={() => pull(focused)}
                        className="flex shrink-0 items-center gap-2 rounded-sm bg-paper-manila px-2.5 py-1 font-condensed text-[0.8rem] font-bold uppercase tracking-[0.15em] text-ink-black shadow hover:brightness-105"
                      >
                        Use <span aria-hidden>→</span>
                        <Kbd>⏎</Kbd>
                      </button>
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
